import React from "react";
import { Avatar, Box, Button, Chip, Stack, Typography } from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Link } from "react-router-dom";

import { getTalentProfileImageUrl, StatusChip, toTitleCase } from "../networkPortalShared";

export function getAssignmentActionVisibility(status) {
  const normalized = String(status || "").trim().toLowerCase();
  return {
    accept: ["assigned", "pending", "offered"].includes(normalized),
    start: normalized === "accepted",
    decline: ["assigned", "pending", "offered", "accepted"].includes(normalized),
  };
}

export function AssignmentBreadcrumb({ title, to = "/talent/assignments" }) {
  return (
    <nav className="talent-assignment-detail__breadcrumb" aria-label="Breadcrumb">
      <Link to={to}>Assignments</Link>
      <span aria-hidden="true">›</span>
      <span aria-current="page" title={title}>{title}</span>
    </nav>
  );
}

export function AssignmentPanel({ title, subtitle, children, className = "", actions = null }) {
  return (
    <section className={`talent-assignment-detail__panel ${className}`.trim()}>
      {title || subtitle || actions ? (
        <header className="talent-assignment-detail__panel-header">
          <div>
            {title ? <Typography component="h2" className="talent-assignment-detail__panel-title">{title}</Typography> : null}
            {subtitle ? <Typography className="talent-assignment-detail__panel-subtitle">{subtitle}</Typography> : null}
          </div>
          {actions}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function AssignmentHero({ assignment, detail, workRequest, member, previewMode = false }) {
  const status = detail?.status || assignment?.status;
  const title = workRequest?.title || `Assignment #${assignment?.id}`;
  const signedInParts = [
    member?.displayName,
    member?.email,
    member?.companyName,
    member?.memberType ? toTitleCase(member.memberType) : null,
  ].filter(Boolean);

  return (
    <section className="talent-assignment-detail__hero">
      <Avatar
        className="talent-assignment-detail__hero-avatar"
        src={workRequest?.thumbnailUrl || getTalentProfileImageUrl(member)}
        alt=""
      >
        <AssignmentTurnedInIcon />
      </Avatar>
      <div className="talent-assignment-detail__hero-copy">
        <Typography className="talent-assignment-detail__eyebrow">Talent Portal Assignment</Typography>
        <Typography component="h1" className="talent-assignment-detail__hero-title">{title}</Typography>
        <Typography className="talent-assignment-detail__hero-subtitle">{previewMode ? "Designer-visible assignment details" : "Assigned work request detail"}</Typography>
        {signedInParts.length ? (
          <Typography className="talent-assignment-detail__hero-meta">
            {previewMode ? "Viewing as " : "Signed in as "}{signedInParts.join(" • ")}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap className="talent-assignment-detail__badges">
          <StatusChip value={status} />
          <StatusChip value={detail?.priority || workRequest?.priority || "normal"} />
          {workRequest?.requestType ? <Chip size="small" variant="outlined" label={toTitleCase(workRequest.requestType)} /> : null}
        </Stack>
      </div>
    </section>
  );
}

const summaryIcons = {
  due: CalendarTodayOutlinedIcon,
  requester: PersonOutlineIcon,
  activity: HistoryOutlinedIcon,
};

export function AssignmentSummary({ items }) {
  return (
    <section className="talent-assignment-detail__summary" aria-label="Assignment summary">
      {items.map((item) => {
        const Icon = summaryIcons[item.icon] || HistoryOutlinedIcon;
        return (
          <div className="talent-assignment-detail__summary-item" key={item.label}>
            <span className="talent-assignment-detail__summary-icon" aria-hidden="true"><Icon fontSize="small" /></span>
            <div>
              <Typography className="talent-assignment-detail__summary-label">{item.label}</Typography>
              <Typography className="talent-assignment-detail__summary-value">{item.value || "Not provided"}</Typography>
            </div>
          </div>
        );
      })}
    </section>
  );
}

export function AssignmentActionsCard({ status, busy, onAccept, onStart, onDecline }) {
  const visible = getAssignmentActionVisibility(status);
  return (
    <AssignmentPanel title="Actions" className="talent-assignment-detail__actions-card">
      <Stack spacing={1.25}>
        {visible.accept ? <Button fullWidth variant="contained" disabled={Boolean(busy)} onClick={onAccept}>Accept</Button> : null}
        {visible.start ? <Button fullWidth variant="outlined" disabled={Boolean(busy)} onClick={onStart}>Start work</Button> : null}
        {visible.decline ? <Button fullWidth variant="outlined" color="error" disabled={Boolean(busy)} onClick={onDecline}>Decline</Button> : null}
        {!visible.accept && !visible.start && !visible.decline ? (
          <Box className="talent-assignment-detail__action-complete">
            <StatusChip value={status} />
            <Typography variant="body2" color="text.secondary">No assignment response is required right now.</Typography>
          </Box>
        ) : null}
      </Stack>
    </AssignmentPanel>
  );
}
